import {
  CandidateEditProfileEmpty,
  GetPostionResponse,
  NewCVCandidateResponse,
  Position,
} from 'candidate/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

import { AutocompleteV2ViewModel } from 'shared/components/autocomplete-v2';
import { CountryCitySelectorStore } from 'shared/components/country-city-selector';
import { MultiSelectViewModel } from 'shared/components/multi-select';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';

import { DEFAULT_RESUME_COUNTRY_ID } from '../create-resume.constants';

type WorkAutocompleteItem = {
  id: number | string;
  name: string;
  slug?: string;
};

type WorkAutocompleteResponse = ApiResponseDto<WorkAutocompleteItem[]>;

export type WorkIndustry = {
  id: number | string;
  name: string;
  slug?: string;
};

export type WorkMonth = {
  id: number | string;
  name: string;
};

type WorkExperienceFieldValues = {
  position: string;
  organization: string;
  expCountry: string;
  expCity: string;
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
  stillWorking: boolean;
  achievements: string;
};

type WorkExperienceInitialValues = Partial<WorkExperienceFieldValues> & {
  id?: string;
  positionId?: number | string | null;
  organizationId?: number | string | null;
  expCityId?: number | string | null;
};

export class WorkExperienceItem {
  readonly id: string;
  position = '';
  organization = '';
  fromMonth = '';
  fromYear = '';
  toMonth = '';
  toYear = '';
  stillWorking = false;
  achievements = '';

  readonly positionOptions = observable.array<Position>([]);
  readonly organizationOptions = observable.array<WorkAutocompleteItem>([]);
  readonly positionVM = new AutocompleteV2ViewModel<WorkAutocompleteItem>({
    inputValue: '',
  });
  readonly organizationVM = new AutocompleteV2ViewModel<WorkAutocompleteItem>({
    inputValue: '',
  });
  readonly positionSearchState = new AsyncState<GetPostionResponse>();
  readonly organizationSearchState = new AsyncState<WorkAutocompleteResponse>();
  readonly location: CountryCitySelectorStore;
  readonly industries = new MultiSelectViewModel<WorkIndustry>([], {
    maxSelected: 5,
    getLabel: (value) => value.name,
  });

  private activePositionSearchRequestId: number | null = null;
  private activeOrganizationSearchRequestId: number | null = null;
  private positionSearchRequestId = 0;
  private organizationSearchRequestId = 0;
  private readonly debouncedFetchPositions = debounce((query: string) => {
    this.getPositions(query);
  }, 500);
  private readonly debouncedFetchOrganizations = debounce((query: string) => {
    this.getOrganizations(query);
  }, 500);

  constructor(initialValues: WorkExperienceInitialValues = {}) {
    this.id = initialValues.id ?? uuidv4();
    this.position = initialValues.position ?? '';
    this.organization = initialValues.organization ?? '';
    this.fromMonth = initialValues.fromMonth ?? '';
    this.fromYear = initialValues.fromYear ?? '';
    this.toMonth = initialValues.toMonth ?? '';
    this.toYear = initialValues.toYear ?? '';
    this.stillWorking = initialValues.stillWorking ?? false;
    this.achievements = initialValues.achievements ?? '';
    this.location = new CountryCitySelectorStore({
      countryId: initialValues.expCountry ?? DEFAULT_RESUME_COUNTRY_ID,
      cityId: initialValues.expCityId ?? null,
      cityLabel: initialValues.expCity ?? '',
    });

    makeObservable(this, {
      position: observable,
      organization: observable,
      fromMonth: observable,
      fromYear: observable,
      toMonth: observable,
      toYear: observable,
      stillWorking: observable,
      achievements: observable,
      positionOptions: observable,
      organizationOptions: observable,
      valid: computed,
      dateRangeValid: computed,
      dateRangeError: computed,
      dateYearRangeError: computed,
      dateMonthRangeError: computed,
      isPositionLoading: computed,
      isOrganizationLoading: computed,
      expCountry: computed,
      expCity: computed,
      positionAutocompleteOptions: computed,
      organizationAutocompleteOptions: computed,
      onSearchPosition: action.bound,
      onSelectPosition: action.bound,
      onSearchOrganization: action.bound,
      onSelectOrganization: action.bound,
      setSelectedIndustries: action.bound,
      updateField: action.bound,
    });

    this.positionVM.setInputValue(this.position);
    if (
      initialValues.positionId !== null &&
      initialValues.positionId !== undefined &&
      this.position
    ) {
      this.positionVM.setValue({
        id: initialValues.positionId,
        name: this.position,
      });
    }
    this.organizationVM.setInputValue(this.organization);
    if (
      initialValues.organizationId !== null &&
      initialValues.organizationId !== undefined &&
      this.organization
    ) {
      this.organizationVM.setValue({
        id: initialValues.organizationId,
        name: this.organization,
      });
    }
  }

  get valid(): boolean {
    const hasEndDate =
      this.stillWorking || Boolean(this.toMonth.trim() && this.toYear.trim());

    return Boolean(
      this.position.trim() &&
        this.organization.trim() &&
        this.industries.values.length > 0 &&
        this.expCountry.trim() &&
        this.fromMonth.trim() &&
        this.fromYear.trim() &&
        hasEndDate &&
        this.dateRangeValid &&
        this.achievements.trim()
    );
  }

  get dateRangeValid(): boolean {
    return !this.dateRangeError;
  }

  get dateRangeError(): string | null {
    return this.dateYearRangeError ?? this.dateMonthRangeError;
  }

  get dateYearRangeError(): string | null {
    if (this.stillWorking || !this.fromYear.trim() || !this.toYear.trim()) {
      return null;
    }

    const fromYear = Number(this.fromYear);
    const toYear = Number(this.toYear);

    if (Number.isNaN(fromYear) || Number.isNaN(toYear)) {
      return null;
    }

    return fromYear > toYear
      ? 'Дата початку роботи не може бути пізніше дати закінчення'
      : null;
  }

  get dateMonthRangeError(): string | null {
    if (
      this.stillWorking ||
      !this.fromYear.trim() ||
      !this.toYear.trim() ||
      !this.fromMonth.trim() ||
      !this.toMonth.trim()
    ) {
      return null;
    }

    const fromYear = Number(this.fromYear);
    const toYear = Number(this.toYear);

    if (Number.isNaN(fromYear) || Number.isNaN(toYear) || fromYear !== toYear) {
      return null;
    }

    const fromMonth = Number(this.fromMonth);
    const toMonth = Number(this.toMonth);

    if (Number.isNaN(fromMonth) || Number.isNaN(toMonth)) {
      return null;
    }

    return fromMonth > toMonth
      ? 'Дата початку роботи не може бути пізніше дати закінчення'
      : null;
  }

  get isPositionLoading(): boolean {
    return (
      this.positionSearchState.status === 'pending' &&
      this.activePositionSearchRequestId !== null
    );
  }

  get isOrganizationLoading(): boolean {
    return (
      this.organizationSearchState.status === 'pending' &&
      this.activeOrganizationSearchRequestId !== null
    );
  }

  get expCountry(): string {
    return this.location.countryId;
  }

  get expCity(): string {
    return this.location.cityLabel;
  }

  get positionAutocompleteOptions(): WorkAutocompleteItem[] {
    return withCustomOption(this.positionOptions.slice(), this.positionVM.inputValue);
  }

  get organizationAutocompleteOptions(): WorkAutocompleteItem[] {
    return withCustomOption(
      this.organizationOptions.slice(),
      this.organizationVM.inputValue
    );
  }

  onSearchPosition(query: string): void {
    this.position = query;
    if (!this.positionVM.value || !sameLabel(this.positionVM.value.name, query)) {
      this.positionVM.setValue(null);
    }
    this.positionVM.setInputValue(query);

    if (query.length < 2) {
      this.debouncedFetchPositions.cancel();
      this.positionSearchRequestId += 1;
      this.activePositionSearchRequestId = null;
      this.positionOptions.clear();
      return;
    }

    this.debouncedFetchPositions(query);
  }

  onSelectPosition(item: WorkAutocompleteItem): void {
    this.debouncedFetchPositions.cancel();
    this.positionSearchRequestId += 1;
    this.activePositionSearchRequestId = null;
    this.position = item.name;
    this.positionVM.select(item, item.name);
  }

  onSearchOrganization(query: string): void {
    this.organization = query;
    if (!this.organizationVM.value || !sameLabel(this.organizationVM.value.name, query)) {
      this.organizationVM.setValue(null);
    }
    this.organizationVM.setInputValue(query);

    if (query.length < 2) {
      this.debouncedFetchOrganizations.cancel();
      this.organizationSearchRequestId += 1;
      this.activeOrganizationSearchRequestId = null;
      this.organizationOptions.clear();
      return;
    }

    this.debouncedFetchOrganizations(query);
  }

  onSelectOrganization(item: WorkAutocompleteItem): void {
    this.debouncedFetchOrganizations.cancel();
    this.organizationSearchRequestId += 1;
    this.activeOrganizationSearchRequestId = null;
    this.organization = item.name;
    this.organizationVM.select(item, item.name);
  }

  setSelectedIndustries(values: WorkIndustry[]): void {
    this.industries.values.replace(values);
  }

  updateField<K extends keyof WorkExperienceFieldValues>(
    field: K,
    value: WorkExperienceFieldValues[K]
  ): void {
    switch (field) {
      case 'position':
        this.position = value as string;
        break;
      case 'organization':
        this.organization = value as string;
        break;
      case 'expCountry':
        this.location.setCountryId(value as string);
        break;
      case 'expCity':
        this.location.setCityLabel(value as string);
        break;
      case 'fromMonth':
        this.fromMonth = value as string;
        break;
      case 'fromYear':
        this.fromYear = value as string;
        break;
      case 'toMonth':
        this.toMonth = value as string;
        break;
      case 'toYear':
        this.toYear = value as string;
        break;
      case 'stillWorking':
        this.stillWorking = value as boolean;
        break;
      case 'achievements':
        this.achievements = value as string;
        break;
      default:
        break;
    }

    if (field === 'stillWorking' && value === true) {
      this.toMonth = '';
      this.toYear = '';
    }

    if ((field === 'toMonth' || field === 'toYear') && value) {
      this.stillWorking = false;
    }
  }

  dispose(): void {
    this.debouncedFetchPositions.cancel();
    this.debouncedFetchOrganizations.cancel();
    this.location.dispose();
  }

  private async getPositions(query: string): Promise<void> {
    const requestId = ++this.positionSearchRequestId;
    this.activePositionSearchRequestId = requestId;

    await this.positionSearchState.run(
      () => HttpClient.post<GetPostionResponse>('/candidates/getPositions', { query }),
      {
        onSuccess: (response) => {
          runInAction(() => {
            if (requestId === this.activePositionSearchRequestId && response.status === 200) {
              this.positionOptions.replace(response.response);
              const exactMatch = response.response.find((position) =>
                sameLabel(position.name, this.positionVM.inputValue)
              );

              if (exactMatch) {
                this.positionVM.setValue(exactMatch);
              }
            }
          });
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activePositionSearchRequestId) {
              this.positionOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activePositionSearchRequestId) {
              this.activePositionSearchRequestId = null;
            }
          });
        },
      }
    );
  }

  private async getOrganizations(query: string): Promise<void> {
    const requestId = ++this.organizationSearchRequestId;
    this.activeOrganizationSearchRequestId = requestId;

    await this.organizationSearchState.run(
      () => HttpClient.post<WorkAutocompleteResponse>('/candidates/getCompanies', { query }),
      {
        onSuccess: (response) => {
          runInAction(() => {
            if (
              requestId === this.activeOrganizationSearchRequestId &&
              response.status === 200
            ) {
              this.organizationOptions.replace(response.response);
              const exactMatch = response.response.find((organization) =>
                sameLabel(organization.name, this.organizationVM.inputValue)
              );

              if (exactMatch) {
                this.organizationVM.setValue(exactMatch);
              }
            }
          });
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activeOrganizationSearchRequestId) {
              this.organizationOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activeOrganizationSearchRequestId) {
              this.activeOrganizationSearchRequestId = null;
            }
          });
        },
      }
    );
  }

}

const createExperience = (): WorkExperienceItem =>
  new WorkExperienceItem({ expCountry: DEFAULT_RESUME_COUNTRY_ID });

export class WorkExperienceStore {
  readonly experiences = observable.array<WorkExperienceItem>([createExperience()]);
  readonly years = observable.array<number>([]);
  readonly months = observable.array<WorkMonth>([]);
  readonly countries = observable.array<{ id: number; name: string }>([]);
  readonly industries = observable.array<WorkIndustry>([]);

  constructor() {
    makeObservable(this, {
      experiences: observable,
      valid: computed,
      addExperience: action.bound,
      removeExperience: action.bound,
      updateField: action.bound,
      years: observable,
      months: observable,
      countries: observable,
      industries: observable,
      update: action.bound,
    });
  }

  get valid(): boolean {
    return this.experiences.every((item) => item.valid);
  }

  addExperience(): void {
    this.experiences.push(createExperience());
  }

  removeExperience(id: string): void {
    if (this.experiences.length === 1) {
      return;
    }

    const index = this.experiences.findIndex((item) => item.id === id);
    if (index >= 0) {
      this.experiences[index].dispose();
      this.experiences.splice(index, 1);
    }
  }

  updateField<K extends keyof WorkExperienceFieldValues>(
    id: string,
    field: K,
    value: WorkExperienceFieldValues[K]
  ): void {
    const item = this.experiences.find((experience) => experience.id === id);
    if (!item) {
      return;
    }

    item.updateField(field, value);
  }

  filteredIndustries(experience: WorkExperienceItem): WorkIndustry[] {
    const query = experience.industries.search.trim().toLowerCase();
    const options = this.industries.slice();

    if (!query) {
      return options;
    }

    return options.filter((industry) =>
      industry.name.toLowerCase().includes(query)
    );
  }

  update(
    payload: CandidateEditProfileEmpty | NewCVCandidateResponse,
    options: { hydrateProfile?: boolean } = {}
  ): void {
    const hydrateProfile = options.hydrateProfile ?? true;

    this.years.replace(payload.yearList);
    this.months.replace(payload.months);
    this.countries.replace(payload.countries);
    this.industries.replace(payload.industries);

    if (!hydrateProfile) {
      this.experiences.forEach((experience) => {
        experience.dispose();
      });
      this.experiences.replace([createExperience()]);
      return;
    }

    if (!payload.userdetail?.experiences?.length) {
      return;
    }

    this.experiences.forEach((experience) => {
      experience.dispose();
    });
    this.experiences.clear();

    payload.userdetail.experiences.forEach((experience) => {
      const item = new WorkExperienceItem({
        id: String(experience.id ?? uuidv4()),
        positionId: experience.position?.value ?? null,
        position: experience.position?.label ?? '',
        organizationId: experience.organization?.value ?? null,
        organization: experience.organization?.label ?? '',
        expCountry: String(experience.exp_country ?? ''),
        expCityId: experience.exp_city ? experience.exp_city.value : null,
        expCity: experience.exp_city?.label ?? '',
        fromMonth: String(experience.from_month_exp ?? ''),
        fromYear: String(experience.from_year_exp ?? ''),
        toMonth: String(experience.to_month_exp ?? ''),
        toYear: String(experience.to_year_exp ?? ''),
        stillWorking: Boolean(experience.still_working),
        achievements: experience.achievements ?? '',
      });

      item.setSelectedIndustries(
        (experience.industry ?? []).map((industry) => ({
          id: industry.value,
          name: industry.label,
        }))
      );

      this.experiences.push(item);
    });

    if (!this.experiences.length) {
      this.experiences.push(createExperience());
    }
  }

  dispose(): void {
    this.experiences.forEach((experience) => {
      experience.dispose();
    });
  }
}

function withCustomOption<TItem extends WorkAutocompleteItem>(
  options: TItem[],
  query: string
): WorkAutocompleteItem[] {
  const trimmedQuery = query.trim();

  if (
    trimmedQuery.length >= 2 &&
    !options.some((option) => option.name.toLowerCase() === trimmedQuery.toLowerCase())
  ) {
    return [
      {
        id: `custom:${trimmedQuery}`,
        name: trimmedQuery,
      },
      ...options,
    ];
  }

  return options;
}

function sameLabel(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}
